import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { AdminScreenKey } from '../types/admin-roles';
import { landingRoute } from '../navigation';
import NoScreenAccess from './NoScreenAccess';
import { useFeatureFlags } from '../context/FeatureFlagsContext';

interface RequireScreenProps {
  /** One screen, or several of which any one opens the route (#1113: Finance opens with `finance` or `finance-config`). */
  screen: AdminScreenKey | AdminScreenKey[];
  children: ReactNode;
}

/**
 * Spec 050 — route guard. The gateway is what actually enforces access; this
 * exists so an admin never lands on a screen that would only render errors.
 *
 * Redirects to whatever the admin can open rather than to a fixed /dashboard,
 * since dashboard is itself a grantable screen.
 */
export default function RequireScreen({ screen, children }: RequireScreenProps) {
  const { isSuperAdmin, hasScreen } = useAuth();
  const features = useFeatureFlags();

  const screens = Array.isArray(screen) ? screen : [screen];
  if (screens.some((s) => hasScreen(s))) {
    return <>{children}</>;
  }

  const fallback = landingRoute(isSuperAdmin, hasScreen, features);
  if (!fallback) {
    return <NoScreenAccess />;
  }

  return <Navigate to={fallback} replace />;
}
